import {
  DataTableShell,
  tdClass,
  thClass,
} from "@/features/exports/components/DataTableShell";
import {
  formatInvoiceDateTime,
  formatMoney,
} from "@/domain/invoices/formatInvoice";
import {
  resolveCustomerEmail,
  resolveCustomerName,
} from "@/domain/customers/formatCustomer";
import type { PayLinkRow } from "@/domain/payLinks/payLinkRow";
import { InvoiceStatusBadge } from "@/features/invoices/components/InvoiceStatusBadge";
import { ClickCopyTextCell } from "@/features/invoices/components/ClickCopyTextCell";
import { CustomerEmailCell } from "@/features/invoices/components/CustomerEmailCell";
import { InvoiceIdCell } from "@/features/invoices/components/InvoiceIdCell";
import { InvoiceRowActionsCell } from "@/features/invoices/components/InvoiceRowActionsCell";
import type { CustomerRow } from "@/types/api";
import { cn } from "@/utils/cn";

interface PayLinksTableProps {
  items: PayLinkRow[];
  customers: CustomerRow[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  embedded?: boolean;
  fixedColumnLayout?: boolean;
}

const compactThClass = `${thClass} px-1.5 py-1.5 invoice-cell`;
const compactTdClass = `${tdClass} px-1.5 py-2 invoice-cell`;
const tightThClass = `${compactThClass} col-tight`;
const tightTdClass = `${compactTdClass} col-tight`;
const invoiceIdThClass = `${tightThClass} col-resource-id col-invoice-id`;
const invoiceIdTdClass = `${tightTdClass} col-resource-id col-invoice-id`;
const fillThClass = `${compactThClass} col-fill`;
const fillTdClass = `${compactTdClass} col-fill`;
const actionThClass = `${compactThClass} col-actions`;
const actionTdClass = `${compactTdClass} col-actions`;

function PayLinksFixedColGroup() {
  return (
    <colgroup>
      <col className="invoice-col-check" />
      <col className="invoice-col-invoice" />
      <col className="invoice-col-customer" />
      <col className="invoice-col-flex" />
      <col className="invoice-col-flex" />
      <col className="invoice-col-amount" />
      <col className="invoice-col-status" />
      <col className="invoice-col-date" />
      <col className="invoice-col-date" />
      <col className="invoice-col-actions" />
    </colgroup>
  );
}

export function PayLinksTable({
  items,
  customers,
  selected,
  onToggle,
  embedded = false,
  fixedColumnLayout = false,
}: PayLinksTableProps) {
  const table = (
    <table
      className={cn(
        "invoice-table-compact w-full border-collapse text-left text-sm",
        embedded && !fixedColumnLayout && "min-w-[800px]",
        fixedColumnLayout && "invoice-table-compact--fixed-layout invoice-table-compact--pay-links",
      )}
    >
      {fixedColumnLayout ? <PayLinksFixedColGroup /> : null}
          <thead>
            <tr>
              <th className={`${tightThClass} w-8`} />
              <th className={invoiceIdThClass}>Invoice number</th>
              <th className={fillThClass}>Customer</th>
              <th className={tightThClass}>Customer ID</th>
              <th className={fillThClass}>Email</th>
              <th className={tightThClass}>Amount</th>
              <th className={tightThClass}>Status</th>
              <th className={`${tightThClass} col-date`}>Created</th>
              <th className={`${tightThClass} col-date`}>Due</th>
              <th className={actionThClass}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => {
              const hasUrl = Boolean(row.hosted_url?.trim());
              const name = resolveCustomerName(row, customers);
              const email = resolveCustomerEmail(row, customers);

              return (
                <tr
                  key={row.invoice_id}
                  className={cn(!hasUrl && "opacity-[0.65]")}
                >
                  <td className={`${tightTdClass} w-8`}>
                    <input
                      type="checkbox"
                      checked={selected.has(row.invoice_id)}
                      disabled={!hasUrl}
                      title={!hasUrl ? "No hosted_invoice_url" : undefined}
                      onChange={() => onToggle(row.invoice_id)}
                    />
                  </td>
                  <td className={invoiceIdTdClass}>
                    <InvoiceIdCell
                      id={row.invoice_id}
                      invoiceNumber={row.invoice_number}
                    />
                  </td>
                  <td className={fillTdClass}>
                    <ClickCopyTextCell
                      value={name}
                      resourceLabel="Customer name"
                      variant="name"
                    />
                  </td>
                  <td className={tightTdClass}>
                    <ClickCopyTextCell
                      value={row.customer_id}
                      resourceLabel="Customer ID"
                      variant="meta"
                      tabularNums
                    />
                  </td>
                  <td className={fillTdClass}>
                    <CustomerEmailCell email={email} />
                  </td>
                  <td className={`${tightTdClass} tabular-nums text-sm`}>
                    <span
                      className="block truncate"
                      title={formatMoney(row.amount, row.currency)}
                    >
                      {formatMoney(row.amount, row.currency)}
                    </span>
                  </td>
                  <td className={tightTdClass}>
                    <InvoiceStatusBadge status={row.status} />
                  </td>
                  <td className={`${tightTdClass} col-date cell-date tabular-nums`}>
                    {formatInvoiceDateTime(row.created)}
                  </td>
                  <td className={`${tightTdClass} col-date cell-date tabular-nums`}>
                    {row.due_date != null ? formatInvoiceDateTime(row.due_date) : "—"}
                  </td>
                  <td className={actionTdClass}>
                    <InvoiceRowActionsCell payLinkUrl={row.hosted_url} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
  );

  return (
    <DataTableShell embedded={embedded}>
      {embedded ? table : <div className="overflow-x-auto">{table}</div>}
    </DataTableShell>
  );
}
